import React, { useState, useCallback, useEffect } from 'react';
import { UserDetailHeadingCover, ShortMsgChannelInfo, ChannelInfo, ChannelIcon } from './styles';
import { LoginContainer } from '../../components/Modals/styles';
import { Icon, Tooltip, Input, message } from 'antd';
import Loader from '../../components/Loader';
import { useTranslation } from 'react-i18next';
import TwilioAccConfigModal from '../../components/Modals/TwilioAccConfigModal';
import { WhatsappIcon, SMSIcon, CallIcon } from '../../images/svgIcons';
import REGEX from '../../utils/regex';
import { getTwilioConfig, updateChannelNum } from '../../utils/services/shortMessageTemplateAPI';
import { MANAGE_SHORT_MESSAGE_ACCOUNT } from './TranslationValues';


const NAMESPACES = ['MANAGE_SHORT_MESSAGE_ACCOUNT'];

const TwilioAccountSettings = () => {
  const [loading, setloading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [disableChange, setDisableChange] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [editChannel, setEditChannel] = useState(null);
  const [number, setNumber] = useState({});
  const [updatedNum, setUpdatedNum] = useState({});
  const [configDetails, setConfigDetails] = useState({});
  const { t } = useTranslation(NAMESPACES);

  let channelsList = [
    {
      key: 'whatsapp',
      name: t(MANAGE_SHORT_MESSAGE_ACCOUNT.WHATSAPP_LABEL.key, MANAGE_SHORT_MESSAGE_ACCOUNT.WHATSAPP_LABEL.value),
      desc: t(MANAGE_SHORT_MESSAGE_ACCOUNT.WHATSAPP_DESCRIPTION.key, MANAGE_SHORT_MESSAGE_ACCOUNT.WHATSAPP_DESCRIPTION.value),
      icon: WhatsappIcon,
    },
    {
      key: 'sms',
      name: t(MANAGE_SHORT_MESSAGE_ACCOUNT.SMS_LABEL.key, MANAGE_SHORT_MESSAGE_ACCOUNT.SMS_LABEL.value),
      desc: t(MANAGE_SHORT_MESSAGE_ACCOUNT.SMS_DESCRIPTION.key, MANAGE_SHORT_MESSAGE_ACCOUNT.SMS_DESCRIPTION.value),
      icon: SMSIcon,
    },
    {
      key: 'call',
      name: t(MANAGE_SHORT_MESSAGE_ACCOUNT.CALL_LABEL.key, MANAGE_SHORT_MESSAGE_ACCOUNT.CALL_LABEL.value),
      desc: t(MANAGE_SHORT_MESSAGE_ACCOUNT.CALL_DESCRIPTION.key, MANAGE_SHORT_MESSAGE_ACCOUNT.CALL_DESCRIPTION.value),
      icon: CallIcon,
    },
  ];

  useEffect(() => {
    const fetchData = async () => {
    const response = await getTwilioConfig();
    if (response) {
      if (response.data && response.errors.length === 0) {
        let data = response.data;
        let numByChannel = {
          whatsapp: data.fromWhatsApp,
          sms: data.fromSms,
          call: data.fromCall,
        };
        setNumber({ ...numByChannel });
        setUpdatedNum({ ...numByChannel });
        setConfigDetails({
          id: data.id,
          accountSid: data.accountSid,
          authToken: data.authToken,
        });
      }
      setloading(false);
    }
  }
  fetchData();
  }, []);

  const handleChange = e => {
    const { value, name } = e.target;
    const isValidNum = REGEX.MOBILE_NUM.test(value);
    if (value) {
      setDisableChange(false);
      if (isValidNum) {
        setUpdatedNum({ ...number, [name]: value });
      } else {
        message.error(t(MANAGE_SHORT_MESSAGE_ACCOUNT.INVALID_MOBILE_NUMBER_ERROR_MESSAGE.key, MANAGE_SHORT_MESSAGE_ACCOUNT.INVALID_MOBILE_NUMBER_ERROR_MESSAGE.value));
      }
    } else {
      setDisableChange(true);
      setUpdatedNum({ ...number, [name]: '' });
      message.error(t(MANAGE_SHORT_MESSAGE_ACCOUNT.EMPTY_MOBILE_NUMBER_INPUT_ERROR_MESSAGE.key, MANAGE_SHORT_MESSAGE_ACCOUNT.EMPTY_MOBILE_NUMBER_INPUT_ERROR_MESSAGE.value));
    }
  };

  const updateNumber = useCallback(async () => {
    setEditChannel(null);
    let channels = ['whatsapp', 'sms', 'call'];
    let value = { id: configDetails?.id };
    if (configDetails?.id) {
      for (let i = 0; i < channels.length; i++) {
        if (updatedNum[channels[i]] !== number[channels[i]]) {
          setUpdating(channels[i]);
          value[
            channels[i] === 'whatsapp'
              ? 'fromWhatsApp'
              : channels[i] === 'sms'
              ? 'fromSms'
              : channels[i] === 'call'
              ? 'fromCall'
              : ''
          ] = updatedNum[channels[i]];
        }
      }
      if (value && value.id && (value.fromWhatsApp || value.fromSms || value.fromCall)) {
        const response = await updateChannelNum(value);
        if (response) {
          if (response.errors?.length === 0) {
            message.success(t(MANAGE_SHORT_MESSAGE_ACCOUNT.TWILIO_CONFIG_UPDATED_SUCCESSFULLY.key, MANAGE_SHORT_MESSAGE_ACCOUNT.TWILIO_CONFIG_UPDATED_SUCCESSFULLY.value));
            setNumber({ ...updatedNum });
          } else if (response.errors?.length > 0) {
            setNumber({ ...number });
            setUpdatedNum({ ...number });
          }
          setUpdating(null);
        }
      }
    } else {
      message.warn(t(MANAGE_SHORT_MESSAGE_ACCOUNT.CONFIG_TWILIO_ACCOUNT_WARNING_MESSAGE.key, MANAGE_SHORT_MESSAGE_ACCOUNT.CONFIG_TWILIO_ACCOUNT_WARNING_MESSAGE.value));
    }
  }, [configDetails, number, updatedNum]);

  const cancelChanges = () => {
    setEditChannel(null);
    setNumber({ ...number });
    setUpdatedNum({ ...number });
  };

  const renderChannelInfo = useCallback(
    (channelKey, channelIcon, description) => {
      return (
        <ChannelInfo disabled={disableChange}>
          <ChannelIcon channel={channelKey}>
            <Icon component={channelIcon} />
          </ChannelIcon>
          <p>{description}</p>
          <div className="detail">
            <span>
              <Icon type="phone" />
              {editChannel === channelKey ? (
                <Input name={channelKey} value={updatedNum[channelKey]} onChange={e => handleChange(e)} />
              ) : (
                <p className="number">
                  {updating === channelKey ? <Icon type="loading" /> : number[channelKey]}
                </p>
              )}
            </span>
            {editChannel === channelKey ? (
              <span className="updateNumber">
                <Tooltip title={t(MANAGE_SHORT_MESSAGE_ACCOUNT.CANCEL_UPDATING_NUMBER_TOOLTIP.key, MANAGE_SHORT_MESSAGE_ACCOUNT.CANCEL_UPDATING_NUMBER_TOOLTIP.value)}>
                  <Icon
                    type="close-circle"
                    theme="twoTone"
                    twoToneColor="#ff0000"
                    onClick={() => cancelChanges()}
                  />
                </Tooltip>
                <Tooltip title={t(MANAGE_SHORT_MESSAGE_ACCOUNT.UPDATE_MOBILE_NUMBER_TOOLTIP.key, MANAGE_SHORT_MESSAGE_ACCOUNT.UPDATE_MOBILE_NUMBER_TOOLTIP.value)}>
                  <Icon
                    type="check-circle"
                    theme="twoTone"
                    twoToneColor={disableChange ? '#898989' : '#29b51b'}
                    onClick={() =>
                      disableChange ? message.error(t(MANAGE_SHORT_MESSAGE_ACCOUNT.INVALID_MOBILE_NUMBER_INPUT_ERROR_MESSAGE.key, MANAGE_SHORT_MESSAGE_ACCOUNT.INVALID_MOBILE_NUMBER_INPUT_ERROR_MESSAGE.value)) : updateNumber()
                    }
                  />
                </Tooltip>
              </span>
            ) : (
              <Tooltip title={t(MANAGE_SHORT_MESSAGE_ACCOUNT.EDIT_MOBILE_NUMBER_TOOLTIP.key, MANAGE_SHORT_MESSAGE_ACCOUNT.EDIT_MOBILE_NUMBER_TOOLTIP.value)}>
                <Icon type="edit" onClick={() => setEditChannel(channelKey)} />
              </Tooltip>
            )}
          </div>
        </ChannelInfo>
      );
    },
    [editChannel, number, updatedNum, disableChange, updating],
  );

  return (
    <React.Fragment>
      <UserDetailHeadingCover shortMsg={1}>
        <div>
          <h5>{t(MANAGE_SHORT_MESSAGE_ACCOUNT.MANAGE_SHORT_MESSAGE_TTILE.key, MANAGE_SHORT_MESSAGE_ACCOUNT.MANAGE_SHORT_MESSAGE_TTILE.value)}</h5>
          <p>{t(MANAGE_SHORT_MESSAGE_ACCOUNT.MANAGE_SHORT_MESSAGE_DESCRIPTION.key, MANAGE_SHORT_MESSAGE_ACCOUNT.MANAGE_SHORT_MESSAGE_DESCRIPTION.value)}</p>
          <div className="ldap_config" onClick={() => setShowConfigModal(true)}>
            <p>{t(MANAGE_SHORT_MESSAGE_ACCOUNT.EDIT_CONFIG_BUTTON.key, MANAGE_SHORT_MESSAGE_ACCOUNT.EDIT_CONFIG_BUTTON.value)}</p>
            <Icon type={'edit'} />
          </div>
        </div>
      </UserDetailHeadingCover>
      {loading ? (
        <LoginContainer>
          <Loader />
        </LoginContainer>
      ) : (
        <ShortMsgChannelInfo>
          {channelsList.map(channel => renderChannelInfo(channel.key, channel.icon, channel.desc))}
        </ShortMsgChannelInfo>
      )}
      {showConfigModal && (
        <TwilioAccConfigModal
          subConfigDetails={configDetails}
          onCancel={() => setShowConfigModal(false)}
          number={number}
        />
      )}
    </React.Fragment>
  );
};
export default TwilioAccountSettings;
